import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { sendChatMessage } from '../../services/chatbotApi';

const ChatbotContext = createContext();

const STORAGE_KEY = 'phada_chatbot_state';

/**
 * Provider do chatbot — gerencia estado, mensagens e comunicacao com a API
 */
export function ChatbotProvider({ children }) {
  const location = useLocation();

  // Estado principal
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState(null);

  // Ref para evitar loops
  const isInitialized = useRef(false);

  // Restaurar estado do sessionStorage
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        setMessages(state.messages || []);
        setConversationId(state.conversationId || null);
        setIsOpen(state.isOpen || false);
        setIsMinimized(state.isMinimized || false);
      }
    } catch {
      // Ignorar erro de parse
    }
  }, []);

  // Persistir estado no sessionStorage
  useEffect(() => {
    if (!isInitialized.current) return;

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        messages,
        conversationId,
        isOpen,
        isMinimized
      }));
    } catch {
      // sessionStorage cheio, ignorar
    }
  }, [messages, conversationId, isOpen, isMinimized]);

  /**
   * Envia uma mensagem para o chatbot
   */
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isTyping) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: text.trim(),
      timestamp: new Date().toISOString()
    };

    // Adicionar mensagem do usuario imediatamente
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    setError(null);

    // Zerar unread se o chat estiver aberto
    if (isOpen && !isMinimized) {
      setUnreadCount(0);
    }

    try {
      const context = {
        currentPage: location.pathname,
        sessionloja: localStorage.getItem('sessionloja'),
        idcliente: localStorage.getItem('sessionCodidcliente'),
        idusuario: localStorage.getItem('sessionId'),
        idloja: localStorage.getItem('sessionloja')
      };

      const response = await sendChatMessage(text.trim(), conversationId, context);

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.reply,
        timestamp: new Date().toISOString(),
        actions: response.actions || [],
        toolsUsed: response.toolsUsed || []
      };

      setMessages(prev => [...prev, assistantMessage]);
      setConversationId(response.conversationId);

      // Se o chat estiver minimizado, incrementar unread
      if (isMinimized || !isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erro de conexao. Tente novamente.';
      setError(errorMsg);

      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: `Desculpe, ocorreu um erro: ${errorMsg}`,
        timestamp: new Date().toISOString(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  }, [isTyping, conversationId, location.pathname, isOpen, isMinimized]);

  /**
   * Inicia nova conversa
   */
  const startNewConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setUnreadCount(0);
    setError(null);
  }, []);

  /**
   * Abre o chat
   */
  const openChat = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false);
    setUnreadCount(0);
  }, []);

  /**
   * Minimiza o chat
   */
  const minimizeChat = useCallback(() => {
    setIsMinimized(true);
  }, []);

  /**
   * Fecha o chat
   */
  const closeChat = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
  }, []);

  /**
   * Restaura chat minimizado
   */
  const restoreChat = useCallback(() => {
    setIsMinimized(false);
    setUnreadCount(0);
  }, []);

  const value = {
    // Estado
    isOpen,
    isMinimized,
    messages,
    conversationId,
    isTyping,
    unreadCount,
    error,

    // Acoes
    sendMessage,
    startNewConversation,
    openChat,
    minimizeChat,
    closeChat,
    restoreChat
  };

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  );
}

ChatbotProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Hook para acessar o contexto do chatbot
 */
export function useChatbot() {
  const context = useContext(ChatbotContext);
  if (context === undefined) {
    throw new Error('useChatbot deve ser usado dentro de um ChatbotProvider');
  }
  return context;
}

export default ChatbotContext;
